import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, API_BASE } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Search, Download, ExternalLink } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

function relTime(iso) {
  if (!iso) return "—";
  try { return formatDistanceToNow(new Date(iso), { addSuffix: true }); } catch { return iso; }
}

function DeliveryBadge({ item }) {
  if (item.webhook_gave_up) {
    return (
      <span data-testid="news-status-gave-up" className="text-[10px] font-mono uppercase tracking-widest text-red-400 border border-red-500/50 px-1.5 py-0.5 rounded-sm" title={item.webhook_delivery_error || "gave up after retries"}>
        GAVE UP
      </span>
    );
  }
  if (item.webhook_delivered_at) {
    return (
      <span data-testid="news-status-delivered" className="text-[10px] font-mono uppercase tracking-widest text-emerald-400 border border-emerald-500/40 px-1.5 py-0.5 rounded-sm" title={`delivered ${item.webhook_delivered_at}`}>
        DELIVERED
      </span>
    );
  }
  if (item.webhook_delivery_error) {
    return (
      <span data-testid="news-status-rejected" className="text-[10px] font-mono uppercase tracking-widest text-red-400 border border-red-500/40 px-1.5 py-0.5 rounded-sm" title={item.webhook_delivery_error}>
        REJECTED
      </span>
    );
  }
  return (
    <span data-testid="news-status-pending" className="text-[10px] font-mono uppercase tracking-widest text-amber-400 border border-amber-500/40 px-1.5 py-0.5 rounded-sm" title={`attempts: ${item.webhook_attempts || 0}`}>
      PENDING
    </span>
  );
}

export default function News() {
  const [q, setQ] = useState("");
  const [feedId, setFeedId] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(0);
  const limit = 50;

  const { data: feeds = [] } = useQuery({
    queryKey: ["feeds-simple"],
    queryFn: async () => (await api.get("/feeds")).data,
  });

  const { data, isLoading } = useQuery({
    queryKey: ["news", q, feedId, status, page],
    queryFn: async () => (await api.get("/news", { params: { q: q || undefined, feed_id: feedId || undefined, status: status || undefined, limit, skip: page * limit } })).data,
    keepPreviousData: true,
    refetchInterval: 30000,
  });

  const items = data?.items || [];
  const total = data?.total || 0;

  const download = async (fmt) => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (feedId) params.set("feed_id", feedId);
    params.set("fmt", fmt);
    const url = `${API_BASE}/news/export?${params.toString()}`;
    // fetch with credentials & download
    const res = await fetch(url, { credentials: "include" });
    if (!res.ok) return;
    const blob = await res.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `news.${fmt}`;
    a.click();
  };

  return (
    <div className="space-y-6" data-testid="news-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-zinc-500">Intelligence</div>
          <h1 className="text-2xl md:text-3xl font-mono font-semibold tracking-tight">News Feed</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
            <Input
              data-testid="news-search-input"
              placeholder="Search title or summary"
              value={q}
              onChange={(e) => { setPage(0); setQ(e.target.value); }}
              className="pl-9 w-72 bg-transparent border-zinc-800 rounded-sm font-mono text-sm"
            />
          </div>
          <select
            data-testid="news-feed-filter"
            value={feedId}
            onChange={(e) => { setPage(0); setFeedId(e.target.value); }}
            className="bg-transparent border border-zinc-800 rounded-sm font-mono text-sm px-2 py-2 text-zinc-200 max-w-[220px]"
          >
            <option value="">All feeds</option>
            {feeds.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
          </select>
          <select
            data-testid="news-status-filter"
            value={status}
            onChange={(e) => { setPage(0); setStatus(e.target.value); }}
            className="bg-transparent border border-zinc-800 rounded-sm font-mono text-sm px-2 py-2 text-zinc-200"
            title="Filter by webhook delivery status"
          >
            <option value="">All statuses</option>
            <option value="delivered">Delivered</option>
            <option value="pending">Pending</option>
            <option value="gave_up">Gave up</option>
            <option value="unrecoverable">Rejected 4xx</option>
          </select>
          <Button data-testid="export-csv-btn" variant="outline" className="rounded-sm border-zinc-800 bg-transparent gap-1" onClick={() => download("csv")}>
            <Download className="h-4 w-4" /> CSV
          </Button>
          <Button data-testid="export-json-btn" variant="outline" className="rounded-sm border-zinc-800 bg-transparent gap-1" onClick={() => download("json")}>
            <Download className="h-4 w-4" /> JSON
          </Button>
        </div>
      </div>

      <div className="text-[11px] font-mono text-zinc-500">
        <span data-testid="news-total">{total}</span> items · page {page + 1}
      </div>

      <div className="space-y-2" data-testid="news-list">
        {isLoading && <div className="text-zinc-500 font-mono text-sm">LOADING…</div>}
        {!isLoading && items.length === 0 && (
          <div className="border border-zinc-800 bg-[#0A0A0A] p-8 text-center text-zinc-500 font-mono text-sm rounded-sm">— no items yet —</div>
        )}
        {items.map((it) => (
          <article key={it.item_hash + it.feed_id} className="border border-zinc-800 bg-[#0A0A0A] p-4 rounded-sm hover:border-zinc-700 transition-colors">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <Badge className="rounded-sm bg-transparent border border-zinc-700 text-zinc-300 font-mono uppercase tracking-widest">{it.feed_name || "—"}</Badge>
              <DeliveryBadge item={it} />
              <span className="text-[11px] font-mono text-zinc-500">{it.published_at || relTime(it.fetched_at)}</span>
            </div>
            <h3 className="font-mono font-medium tracking-tight text-white text-base leading-snug">
              <a href={it.link} target="_blank" rel="noreferrer" className="hover:underline inline-flex items-center gap-1.5">
                {it.title || "(no title)"} <ExternalLink className="h-3 w-3 text-zinc-500" />
              </a>
            </h3>
            {it.summary && (
              <p className="mt-2 text-sm text-zinc-400 line-clamp-3" dangerouslySetInnerHTML={{ __html: it.summary }} />
            )}
          </article>
        ))}
      </div>

      <div className="flex items-center justify-between pt-2">
        <Button data-testid="news-prev-btn" variant="outline" className="rounded-sm border-zinc-800 bg-transparent" disabled={page === 0} onClick={() => setPage(p => Math.max(0, p - 1))}>Prev</Button>
        <div className="font-mono text-xs text-zinc-500">page {page + 1} / {Math.max(1, Math.ceil(total / limit))}</div>
        <Button data-testid="news-next-btn" variant="outline" className="rounded-sm border-zinc-800 bg-transparent" disabled={(page + 1) * limit >= total} onClick={() => setPage(p => p + 1)}>Next</Button>
      </div>
    </div>
  );
}
